// ============================================================
// Tie-In — TriageInbox.jsx
// Sorted inbox view. Pulls the most recent run's triage items
// and groups them by tier in priority order.
// ============================================================

import { useState, useEffect } from 'react';
import { t, TIER_META } from './triageTheme.js';
import TierPill from './TierPill.jsx';
import { getHistory } from './triageApi.js';

// ── Draft chip ────────────────────────────────────────────────
function DraftChip() {
  return (
    <div style={{
      display:      'inline-flex',
      alignItems:   'center',
      gap:          '5px',
      fontSize:     '11px',
      color:        t.colors.accent,
      background:   t.colors.accentGlow,
      border:       `1px solid ${t.colors.accentDim}`,
      borderRadius: t.radii.pill,
      padding:      '2px 8px',
      fontFamily:   t.fonts.body,
      marginTop:    '6px',
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20 4L3 11l7 3 3 7 7-17z"/>
      </svg>
      Draft ready in Gmail
    </div>
  );
}

// ── Calendar chip ─────────────────────────────────────────────
function CalendarChip() {
  return (
    <div style={{
      display:      'inline-flex',
      alignItems:   'center',
      gap:          '5px',
      fontSize:     '11px',
      color:        t.colors.thisWeek,
      background:   t.colors.thisWeekDim,
      border:       `1px solid ${t.colors.thisWeekBorder}`,
      borderRadius: t.radii.pill,
      padding:      '2px 8px',
      fontFamily:   t.fonts.body,
      marginTop:    '6px',
      marginLeft:   '6px',
    }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/>
        <line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
      </svg>
      On calendar
    </div>
  );
}

// ── Email card ────────────────────────────────────────────────
function EmailCard({ item }) {
  const meta = TIER_META[item.tier] || TIER_META.LOW;

  return (
    <div style={{
      background:   t.colors.bgPanel,
      border:       `1px solid ${item.tier === 'LEAK' ? t.colors.leakBorder : t.colors.border}`,
      borderRadius: t.radii.lg,
      padding:      '12px 14px',
      marginBottom: '8px',
      borderLeft:   item.tier === 'LEAK' ? `3px solid ${t.colors.leak}` : undefined,
    }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', marginBottom: '4px' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <span style={{ fontSize: '13px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body }}>
            {item.sender_name || item.sender_email || 'Unknown'}
          </span>
          {item.sender_email && item.sender_name && (
            <span style={{ fontSize: '11px', color: t.colors.textMuted, fontFamily: t.fonts.mono, marginLeft: '6px' }}>
              {item.sender_email}
            </span>
          )}
        </div>
        <TierPill tier={item.tier} />
      </div>

      <div style={{
        fontSize:     '13px',
        color:        t.colors.textPrimary,
        fontFamily:   t.fonts.body,
        marginBottom: '3px',
        fontWeight:   item.tier === 'LEAK' || item.tier === 'EOD' ? 500 : 400,
      }}>
        {item.subject || '(no subject)'}
      </div>

      {item.tier_reasoning && (
        <div style={{ fontSize: '11px', color: t.colors.textSecondary, fontFamily: t.fonts.body, lineHeight: 1.4, marginBottom: '2px' }}>
          {item.tier_reasoning}
        </div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0' }}>
        {item.draft_created && <DraftChip />}
        {item.block_created && <CalendarChip />}
      </div>

      {item.followup_stage && (
        <div style={{
          marginTop:    '6px',
          fontSize:     '11px',
          color:        t.colors.warning,
          fontFamily:   t.fonts.mono,
          background:   t.colors.warningDim,
          border:       `1px solid ${t.colors.warning}44`,
          borderRadius: t.radii.sm,
          padding:      '3px 8px',
          display:      'inline-block',
        }}>
          {item.followup_stage === 'overdue_24h' ? '⚠ Overdue 24h' : '⚠ Overdue 48h'}
        </div>
      )}
    </div>
  );
}

// ── Tier section ──────────────────────────────────────────────
function TierSection({ tier, items }) {
  if (!items.length) return null;
  const meta = TIER_META[tier];

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        display:       'flex',
        alignItems:    'center',
        gap:           '8px',
        marginBottom:  '10px',
      }}>
        <div style={{
          width:  '3px',
          height: '16px',
          borderRadius: t.radii.pill,
          background: meta.color,
          flexShrink: 0,
        }} />
        <span style={{
          fontSize:      '12px',
          fontWeight:    600,
          fontFamily:    t.fonts.mono,
          color:         meta.textColor,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
        }}>
          {meta.label}
        </span>
        <span style={{ fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.mono }}>
          {items.length}
        </span>
      </div>
      {items.map(item => <EmailCard key={item.id} item={item} />)}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────
export default function TriageInbox({ userId }) {
  const [items,   setItems]   = useState([]);
  const [runAt,   setRunAt]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  useEffect(() => {
    if (!userId) return;
    getHistory(userId, 1, 1)
      .then(data => {
        const lastRun = data.runs?.[0];
        if (lastRun) {
          setItems(lastRun.triage_items || []);
          setRunAt(lastRun.run_at);
        }
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [userId]);

  // Group by tier in priority order
  const TIER_ORDER = ['LEAK', 'EOD', 'THIS_WEEK', 'EXPENSE', 'LOW'];
  const grouped = {};
  for (const tier of TIER_ORDER) grouped[tier] = [];
  for (const item of items) {
    if (grouped[item.tier]) grouped[item.tier].push(item);
  }

  const hasItems = items.length > 0;

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 12px 80px' }}>

      {/* Last run timestamp */}
      {runAt && (
        <div style={{
          display:      'flex',
          alignItems:   'center',
          gap:          '6px',
          padding:      '8px 12px',
          background:   t.colors.bgPanel,
          borderRadius: t.radii.md,
          marginBottom: '16px',
          border:       `1px solid ${t.colors.border}`,
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={t.colors.textMuted} strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/>
          </svg>
          <span style={{ fontSize: '12px', color: t.colors.textMuted, fontFamily: t.fonts.body }}>
            Last triaged {new Date(runAt).toLocaleString('en-US', {
              weekday: 'short', month: 'short', day: 'numeric',
              hour: 'numeric', minute: '2-digit',
            })}
          </span>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: t.colors.textMuted, fontFamily: t.fonts.body, fontSize: '14px' }}>
          Loading...
        </div>
      ) : error ? (
        <div style={{ textAlign: 'center', padding: '40px 0', color: t.colors.danger, fontFamily: t.fonts.body, fontSize: '14px' }}>
          {error}
        </div>
      ) : !hasItems ? (
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{ fontSize: '32px', marginBottom: '12px' }}>✓</div>
          <div style={{ fontSize: '15px', fontWeight: 500, color: t.colors.textPrimary, fontFamily: t.fonts.body, marginBottom: '6px' }}>
            Inbox is clear
          </div>
          <div style={{ fontSize: '13px', color: t.colors.textSecondary, fontFamily: t.fonts.body, lineHeight: 1.5 }}>
            No emails from the last run.<br/>Next run is automatic.
          </div>
        </div>
      ) : (
        TIER_ORDER.map(tier => (
          <TierSection key={tier} tier={tier} items={grouped[tier]} />
        ))
      )}
    </div>
  );
}
